import { useEffect, useMemo, useState } from 'react';
import type {
  DataTableCellIdCoordinate,
  DataTableCellIndexCoordinate,
  DataTableCellReference,
  DataTableService,
  DataTableStateChangeEvent,
} from '@/components/UI/DataTableV2/DataTable.model';
import { DataTableEvent } from '@/components/UI/DataTableV2/DataTable.model';
import { toCellRef } from '../DataTable.util';
import { useDataTable } from '../DataTableProvider';

export interface UseCellCoordinateReturn {
  idCoordinate: DataTableCellIdCoordinate | undefined;
  cellRef: DataTableCellReference;
}

/**
 * Computes cell coordinate from index coordinates
 *
 * Converts cell index coordinates (row/column numbers) to ID coordinates (API IDs)
 * and generates the corresponding cell reference string.
 *
 * @param dataTableService - DataTable service instance
 * @param coordinate - Cell index coordinate (column and row numbers)
 * @returns Object containing ID coordinate and cell reference string
 */
const getCellCoordinate = (
  dataTableService: DataTableService,
  coordinate: DataTableCellIndexCoordinate
): UseCellCoordinateReturn => {
  const idCoordinate = dataTableService.fromIndexCoordinate(coordinate);
  const cellRef = toCellRef(idCoordinate);
  return { idCoordinate, cellRef };
};

/**
 * Transform index coordinates to ID coordinates
 *
 * This hook converts cell index coordinates (row/column numbers) to ID coordinates
 * (API IDs) and generates the cell reference string used for event subscriptions.
 *
 * Uses useMemo with coordinate dependencies to derive result immediately on coordinate changes (no stale renders).
 * Version counter invalidates memo when internal row/column mappings change.
 *
 * @param coordinate - Cell index coordinate (column and row numbers)
 * @returns Object containing the ID coordinate and cell reference string (memoized for reference stability)
 */
export const useCellCoordinate = (coordinate: DataTableCellIndexCoordinate): UseCellCoordinateReturn => {
  const { dataTableService } = useDataTable();
  const [renderForced, triggerRender] = useState(true);

  useEffect(() => {
    const abortController = new AbortController();

    const stateChangeListener = (event: DataTableStateChangeEvent) => {
      if (event.detail.type.has('rows') || event.detail.type.has('columns')) {
        triggerRender((prev) => !prev);
      }
    };

    dataTableService.subscribe(DataTableEvent.TableStateChange, stateChangeListener, abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [dataTableService]);

  return useMemo(() => getCellCoordinate(dataTableService, coordinate), [dataTableService, coordinate, renderForced]);
};
